import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers';

import ModalOverlay from 'components/ui/Overlays/ModalOverlay/ModalOverlay';
import Button from 'components/ui/Button';
import Form, {FormOnSubmit, WithFormError} from 'components/ui/Form/Form/Form';
import useRestrictedActivitiesModal from './useRestrictedActivitiesModal';
import AccordionItem, {Accordion} from 'components/ui/Accordion/Accordion';
import {createFaqLink} from 'helpers/url';
import Radio from 'components/ui/Form/Radio/Radio';
import styles from './ProhibitedActivitesModal.module.scss';

interface IRestrictedActivitesModalFormValues {
    restrictedActivity?: string;
}

interface IRestrictedActivitesModalProps {
    onAccept: (restrictedActivity: boolean) => void;
}

const DECLARATION_OPTIONS = [
    {
        label: 'One or more of these apply to me/my business',
        value: 'yes',
    },
    {
        label: 'None of these apply to me/my business',
        value: 'no',
    },
];

const RestrictedActivitesModal = ({
    onAccept,
}: IRestrictedActivitesModalProps) => {
    const {closeModal, selectedLanguageParams} = useRestrictedActivitiesModal();

    const DEFAULT_FORM_VALUES: IRestrictedActivitesModalFormValues = {
        restrictedActivity: undefined,
    };

    const formMethods = useForm<
        WithFormError<IRestrictedActivitesModalFormValues>
    >({
        defaultValues: DEFAULT_FORM_VALUES,
    });
    const {watch} = formMethods;
    const restrictedActivity = watch('restrictedActivity');

    const onSubmit: FormOnSubmit<
        IRestrictedActivitesModalFormValues
    > = values => {
        onAccept(values.restrictedActivity === 'yes');
    };

    const onCancel = () => {
        closeModal();
    };

    return (
        <ModalOverlay doNotCloseOnOutsideClick scrollable closeIcon={false}>
            <div className={styles.container}>
                <Form<IRestrictedActivitesModalFormValues>
                    className={styles.formContainer}
                    onSubmit={onSubmit}
                    formMethods={formMethods}
                >
                    <div className={styles.title}>
                        <h3>
                            Restricted Business Activities and Payment Flows
                        </h3>
                    </div>

                    <p>
                        If your company operates in any of the industries or
                        business activities listed below, additional information
                        may be required during onboarding. Some services, such
                        as virtual IBANs, may not be available, and onboarding
                        may be subject to management approval.
                    </p>
                    <div>
                        <Accordion toggleMode="single">
                            <AccordionItem
                                title={<>Financial & Investment Services</>}
                                accordionKey="financialInvestmentServices"
                                toggleType="title"
                                className={styles.box}
                                titleClass={styles.title}
                                body={
                                    <>
                                        Including:
                                        <ul>
                                            <li>Financial services</li>
                                            <li>
                                                Crowdfunding, crowdlending, and
                                                similar activities
                                            </li>
                                            <li>
                                                Non-fiat currency/crypto,
                                                virtual assets
                                            </li>
                                            <li>
                                                Bidding fee auctions (e.g.,
                                                penny auctions)
                                            </li>
                                            <li>
                                                Sale or marketing of Binary
                                                Options
                                            </li>
                                            <li>
                                                Holding companies of private
                                                individuals
                                            </li>
                                            <li>
                                                Private-to-private money
                                                remittance involving cash
                                            </li>
                                        </ul>
                                    </>
                                }
                            />
                            <AccordionItem
                                title={<>High-Value Goods & Commodities</>}
                                accordionKey="highValueGoodsAndCommodities"
                                toggleType="title"
                                className={styles.box}
                                titleClass={styles.title}
                                body={
                                    <div>
                                        Including
                                        <ul>
                                            <li>
                                                Precious metals and stones
                                                dealers
                                            </li>
                                            <li>Arts and antiques</li>
                                            <li>Sale of vehicles</li>
                                            <li>
                                                Used automobile dealers and
                                                sellers of used car parts
                                            </li>
                                            <li>Other valuable commodities</li>
                                        </ul>
                                    </div>
                                }
                            />
                            <AccordionItem
                                title={
                                    <>Legal, Consulting & Corporate Services </>
                                }
                                accordionKey="legalConsultionAndCorporateServices"
                                toggleType="title"
                                className={styles.box}
                                titleClass={styles.title}
                                body={
                                    <div>
                                        Including:
                                        <ul>
                                            <li>Accounting</li>
                                            <li>Legal services</li>
                                            <li>
                                                Corporate service providers
                                                (e.g., company formation,
                                                registered office provision,
                                                secretarial services)
                                            </li>
                                            <li>Affiliate marketing schemes</li>
                                            <li>Special Purpose Vehicles</li>
                                            <li>(SPVs)</li>
                                            <li>Labor brokers</li>
                                            <li> Debt collection</li>
                                        </ul>
                                    </div>
                                }
                            />
                            <AccordionItem
                                title={<>Security & Defense</>}
                                accordionKey="securityAndDefense"
                                toggleType="title"
                                className={styles.box}
                                titleClass={styles.title}
                                body={
                                    <div>
                                        Including:
                                        <ul>
                                            <li>
                                                Private security firms and
                                                private military contractors
                                            </li>
                                            <li>
                                                Sales involving weapons
                                                (including knives), ammunition,
                                                defense equipment, or military
                                                vehicles
                                            </li>
                                        </ul>
                                    </div>
                                }
                            />
                            <AccordionItem
                                title={<>Charities, Politics & Diplomacy </>}
                                accordionKey="charitiesPoliticsAndDiplomacy"
                                toggleType="title"
                                className={styles.box}
                                titleClass={styles.title}
                                body={
                                    <div>
                                        Including:
                                        <ul>
                                            <li>Charities and NGOs</li>
                                            <li>
                                                mbassies, diplomatic missions,
                                                and consulates
                                            </li>
                                            <li>Political organizations</li>
                                        </ul>
                                    </div>
                                }
                            />
                            <AccordionItem
                                title={<>Retail & Adult Content </>}
                                accordionKey="retailAndAdultContent"
                                toggleType="title"
                                className={styles.box}
                                titleClass={styles.title}
                                body={
                                    <div>
                                        Including:
                                        <ul>
                                            <li>Marketplaces</li>
                                            <li>
                                                exually explicit content (e.g.,
                                                sex shops, adult retailers)
                                            </li>
                                        </ul>
                                    </div>
                                }
                            />
                        </Accordion>
                        <p>
                            For more details on the restricted industries,
                            please refer to{' '}
                            <a
                                href={createFaqLink(
                                    selectedLanguageParams,
                                    '/articles/6554682-restricted-industries-business-activities',
                                )}
                                target="_blank"
                                rel="noreferrer noopener"
                            >
                                Restricted Industries and Activities.
                            </a>
                        </p>
                        <p>
                            <b>
                                I declare that I have read and understood the
                                information above about restricted business
                                activities and payment flows. I confirm that the
                                information provided is accurate, and I
                                understand that inaccuracies may lead to account
                                termination.
                            </b>
                        </p>
                    </div>
                    <div>
                        <div>
                            <Radio
                                horizontal
                                name="restrictedActivity"
                                options={DECLARATION_OPTIONS}
                                registerOptions={{required: 'Required'}}
                            />
                        </div>
                        <div className={styles.buttonRow}>
                            <Button
                                type="submit"
                                disabled={!restrictedActivity}
                            >
                                Continue
                            </Button>
                            <Button variant="text" onClick={onCancel}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </Form>
            </div>
        </ModalOverlay>
    );
};

export default RestrictedActivitesModal;
